import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { formatPhoneNumber } from "react-phone-number-input";

import CircleButton from "@components/buttons/CircleButton";
import CardBase from "@components/cards/CardBase";
import { InboxContext } from "@components/messaging/InboxProvider";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import useContextSafely from "@hooks/useContextSafely";
import { ResourceFragment, WorksheetFragment } from "@typing/Generated";

type Props = {
  handleRemoveRecord: () => void;
  record: ResourceFragment | WorksheetFragment;
};

// This is a slightly confusing name, because there are two different types of "attachments" for
// messages. This type happens when you are sending a message "about" a resource or worksheet, in
// which case we display a preview of that thing in your message box and turn it into a link to that
// thing when you send the message. The other type of "attachment" is a standard file attachment,
// and those can be added to any type of RichTextarea that turns on the attachments control.
const MessageAttachment = ({ handleRemoveRecord, record }: Props) => {
  const { journey } = useContextSafely(InboxContext);

  return (
    <CardBase
      body={
        <IonGrid className="ion-no-padding">
          <IonRow className="ion-align-items-center">
            <IonCol>
              <BodyCopy bold color="primary">
                {record.title}
              </BodyCopy>
            </IonCol>
            <IonCol size="auto">
              <CircleButton
                ariaLabelKey="dictionary.delete"
                color="gray-200"
                icon="xmark"
                onClick={handleRemoveRecord}
              />
            </IonCol>
          </IonRow>
          {record.__typename === "Resource" && (
            <>
              {record.cost && (
                <IonRow>
                  <IonCol>
                    <div className="flex margin-4-top">
                      <div className="ion-margin-end">
                        <Icon color="gray-200" icon="moneyBill" />
                      </div>
                      <BodyCopy color="gray" size="small">
                        {record.cost}
                      </BodyCopy>
                    </div>
                  </IonCol>
                </IonRow>
              )}
              {record.phoneNumber && (
                <IonRow>
                  <IonCol>
                    <div className="flex margin-4-top">
                      <div className="ion-margin-end margin-4-top">
                        <Icon color="gray-200" icon="phone" />
                      </div>
                      <ExternalLink
                        href={`tel:${record.phoneNumber}`}
                        linkEventCreateVariables={{ journeyId: journey.id }}
                      >
                        {formatPhoneNumber(record.phoneNumber)
                          ? formatPhoneNumber(record.phoneNumber)
                          : record.phoneNumber}
                      </ExternalLink>
                    </div>
                  </IonCol>
                </IonRow>
              )}
              {record.linkUrl && record.linkTitle && (
                <IonRow>
                  <IonCol>
                    <div className="flex margin-4-top">
                      <div className="ion-margin-end margin-4-top">
                        <Icon color="gray-200" icon="globe" />
                      </div>
                      <ExternalLink href={record.linkUrl} linkEventCreateVariables={{ journeyId: journey.id }}>
                        {record.linkTitle}
                      </ExternalLink>
                    </div>
                  </IonCol>
                </IonRow>
              )}
            </>
          )}
        </IonGrid>
      }
    />
  );
};

export default MessageAttachment;
